import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import { RoutePaths } from "../../../utility/RoutePaths";
import {
  getCoursesAdminAPI,
  createCourseAPI,
  updateCourseAPI,
  deleteCourseAPI,
} from "../../../services/portal/admin/courseService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import { tableHeaderColor } from "../../../utility/Common";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";

const ManageCourses = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [editingDetails, setEditingDetails] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    fee: "",
  });

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const res = await getCoursesAdminAPI();
      if (res && res.IsSuccessful && res.Data) {
        setCourses(res.Data);
      }
    } catch (error) {
      console.error("Error loading courses:", error);
      showErrorToastMsg("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showErrorToastMsg("Please select a valid image file");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImageBase64(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showErrorToastMsg("Please enter a title");
      return;
    }

    if (!formData.description.trim()) {
      showErrorToastMsg("Please enter a description");
      return;
    }

    if (!formData.fee) {
      showErrorToastMsg("Please enter a fee");
      return;
    }

    if (!imageFile && !editingDetails) {
      showErrorToastMsg("Please select an image");
      return;
    }

    setSaving(true);
    try {
      let res;
      if (editingDetails) {
        var params = {
          CourseId: editingDetails.CourseId,
          Title: formData.title.trim(),
          Description: formData.description.trim(),
          StartDate: "2026-06-19T15:32:05.814Z",
          EndDate: "2026-06-19T15:32:05.814Z",
          CourseFee: parseFloat(formData.fee),
          ...(imageBase64 && { Image: imageBase64 }),
        }

        res = await updateCourseAPI(params);
      } else {
        var params = {
          CourseId: 0,
          Title: formData.title.trim(),
          Description: formData.description.trim(),
          StartDate: "2026-06-19T15:32:05.814Z",
          EndDate: "2026-06-19T15:32:05.814Z",
          CourseFee: parseFloat(formData.fee),
          Status: 1,
          Image: imageBase64,
        }

        res = await createCourseAPI(params);
      }

      if (res && res.IsSuccessful) {
        showSuccessToastMsg(
          res.Message ||
            (editingDetails ? "Updated successfully" : "Saved successfully"),
        );
        setEditingDetails(null);
        clearFields();
        loadCourses();
      } else {
        showErrorToastMsg(
          res?.Message || "Something went wrong, please try again",
        );
      }
    } catch (error) {
      console.error("Error saving course:", error);
      showErrorToastMsg("Failed to save course");
    } finally {
      setSaving(false);
    }
  };

  const handleEditDetails = (item) => {
    setEditingDetails(item);
    setFormData({
      title: item.Title,
      description: item.Description,
      fee: item.CourseFee !== undefined && item.CourseFee !== null ? String(item.CourseFee) : "",
    });
  };

  const handleCancelEdit = () => {
    setEditingDetails(null);
    clearFields();
  };

  const clearFields = () => {
    setFormData({
      title: "",
      description: "",
      fee: "",
    });
    setImageFile(null);
    setImageBase64(null);
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const res = await deleteCourseAPI(deleteItem.CourseId);
      if (res.IsSuccessful) {
        loadCourses();
        showSuccessToastMsg("Deleted successfully");
      } else {
        showErrorToastMsg(res.Message || "Failed to delete");
      }
      setShowDeleteConfirm(false);
      setDeleteItem(null);
    } catch (error) {
      console.error("Error deleting course:", error);
      showErrorToastMsg("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  return (
    <DashboardLayout title="Manage Courses" subtitle="Add and manage courses">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          Manage Courses
        </h3>

        {/* Add/Edit Form */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              {editingDetails ? "Edit Course" : "Add New Course"}
            </h4>
          </div>
          <div className="p-6">
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter title"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fee (Rs.) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="fee"
                  value={formData.fee}
                  onChange={handleChange}
                  placeholder="Enter fee"
                  min="0"
                  step="0.01"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image {!editingDetails && <span className="text-red-500">*</span>}
                  {editingDetails && <span className="text-gray-400 font-normal"> (leave blank to keep current)</span>}
                </label>
                {editingDetails && editingDetails.Image && !imageFile && (
                  <img
                    src={editingDetails.Image}
                    alt="Current"
                    className="w-16 h-16 object-cover rounded mb-1"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {imageFile && (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xs text-gray-600">{imageFile.name}</span>
                    <button
                      type="button"
                      onClick={() => setImageFile(null)}
                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-2 items-end justify-end md:col-span-3">
                {editingDetails && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="text-sm font-medium px-4 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className={`text-md font-medium px-4 py-1.5 rounded-md transition-colors ${
                    saving
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-primary-600 text-white hover:bg-primary-700"
                  }`}
                >
                  {saving ? "Saving..." : editingDetails ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Courses Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">Courses List</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Image
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee (Rs.)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manage Files
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manage Students
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : courses.length === 0 ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No courses found.
                    </td>
                  </tr>
                ) : (
                  courses.map((item) => (
                    <tr key={item.Id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.Image ? (
                          <img
                            src={item.Image}
                            alt="Course"
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <img
                            src={"/images/no_image.jpg"}
                            alt="Course"
                            className="w-16 h-16 object-cover rounded"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.Title}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.Description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.CourseFee !== undefined && item.CourseFee !== null
                          ? item.CourseFee.toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <button
                          onClick={() =>
                            navigate(
                              `${RoutePaths.ADMIN_MANAGE_COURSE_FILES_PAGE}?courseId=${item.CourseId}`,
                            )
                          }
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Files
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <button
                          onClick={() =>
                            navigate(
                              `${RoutePaths.ADMIN_MANAGE_COURSE_STUDENTS_PAGE}?courseId=${item.CourseId}`,
                            )
                          }
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Students
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-4">
                        <button
                          onClick={() => handleEditDetails(item)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showDeleteConfirm && (
          <RemoveConfirmationDialog
            loading={loading}
            description={deleteItem.Title}
            cancelDelete={cancelDelete}
            confirmDelete={confirmDelete}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ManageCourses;
